import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Form from '@hero/ui-kit/inputs/Form';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import React from 'react';

import * as yup from 'yup';
import useProspectAction from '../../../api/useProspectAction';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';

const deactivateSchema = yup
    .object()
    .shape({
        note: yup.string().nullable().notRequired()
    })
    .defined();

export type DeactivateParams = yup.InferType<typeof deactivateSchema>;

type DeactivateModalProps = {
    onCancel?: () => void;
    prospectId: string;
} & Pick<ModalProps, 'externalControls'>;

const DeactivateModal: React.FC<DeactivateModalProps> = ({
    externalControls,
    onCancel,
    prospectId
}) => {
    const [, setExternalState] = externalControls ?? [];

    const {
        mutateAsync: prospectAction,
        isPending: isActionPending,
        error,
        isSuccess,
        reset
    } = useProspectAction();

    const handleCancel = () => {
        onCancel?.();
        setExternalState && setExternalState(false);
        reset();
    };

    const handleSubmit = (values: DeactivateParams) => {
        prospectAction({
            prospect_id: prospectId,
            action: 'deactivate',
            ...(values.note && { note: values.note })
        });
    };

    return (
        <Modal
            isCancelable
            onClose={handleCancel}
            externalControls={externalControls}
            type={isSuccess ? 'success' : 'neutral'}
        >
            <P centered strong size="large">
                Deactivate
            </P>

            <Section centered noDefaultPadding>
                {isSuccess ? (
                    <>
                        <P size="large" centered>
                            Prospect successfully deactivated.
                        </P>
                        <Button type="button" variant="outlineSecondary" onClick={handleCancel}>
                            Close
                        </Button>
                    </>
                ) : (
                    <>
                        {isActionPending ? (
                            <InlineLoader />
                        ) : (
                            <Form
                                validationSchema={deactivateSchema}
                                submitFn={handleSubmit}
                                defaultValues={{
                                    action: 'called_successful'
                                }}
                            >
                                <TextArea name="note" displayName="New Note" cols={40} rows={8} />
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        columnGap: '1rem',
                                        marginTop: '1.6rem'
                                    }}
                                >
                                    <Button
                                        type="button"
                                        variant="outlineSecondary"
                                        onClick={handleCancel}
                                        noDefaultMargin
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isActionPending}
                                        noDefaultMargin
                                    >
                                        Submit
                                    </Button>
                                </div>

                                {error ? <QueryInlineError centered error={error} /> : null}
                            </Form>
                        )}
                    </>
                )}
            </Section>
        </Modal>
    );
};

export default DeactivateModal;
